import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, PencilSimple, ArrowCounterClockwise, Check, X } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SystemEmailsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editKey, setEditKey] = useState(null);
  const [editForm, setEditForm] = useState({ subject: '', body: '', enabled: true });
  const [previewKey, setPreviewKey] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/email-templates`);
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const toggleEnabled = async (item) => {
    try {
      await axios.put(`${API}/api/admin/email-templates/${item.key}`, { enabled: !item.enabled });
      toast.success(item.enabled ? 'Email desativado' : 'Email ativado');
      fetchItems();
    } catch { toast.error('Erro ao atualizar'); }
  };

  const openEdit = (item) => {
    setEditKey(item.key);
    setEditForm({ subject: item.subject, body: item.body, enabled: item.enabled });
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/api/admin/email-templates/${editKey}`, editForm);
      toast.success('Template guardado');
      setEditKey(null);
      fetchItems();
    } catch { toast.error('Erro ao guardar'); }
  };

  const resetTemplate = async (key) => {
    if (!window.confirm('Repor template original? As tuas alterações serão perdidas.')) return;
    try {
      await axios.post(`${API}/api/admin/email-templates/${key}/reset`);
      toast.success('Template reposto');
      if (editKey === key) setEditKey(null);
      fetchItems();
    } catch { toast.error('Erro'); }
  };

  const openPreview = async (key) => {
    try {
      const { data } = await axios.post(`${API}/api/admin/email-templates/${key}/preview`, { variables: {} });
      setPreviewSubject(data.subject || '');
      setPreviewHtml(data.body || '');
      setPreviewKey(key);
    } catch { toast.error('Erro ao gerar pré-visualização'); }
  };

  const previewFromEditor = async () => {
    try {
      // Save first if dirty, then preview
      await axios.put(`${API}/api/admin/email-templates/${editKey}`, editForm);
      const { data } = await axios.post(`${API}/api/admin/email-templates/${editKey}/preview`, { variables: {} });
      setPreviewSubject(data.subject || '');
      setPreviewHtml(data.body || '');
      setPreviewKey(editKey);
    } catch { toast.error('Erro a gerar preview'); }
  };

  const currentEditItem = items.find(i => i.key === editKey);

  if (loading) return <p className="text-xs text-brand-muted text-center py-8">A carregar...</p>;

  return (
    <div data-testid="system-emails-tab">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-900">
        <p><b>Emails automáticos do sistema.</b> Estas mensagens são enviadas automaticamente pela aplicação. Podes desativar individualmente, editar o assunto e o corpo (HTML), e pré-visualizar antes de guardar.</p>
      </div>

      <div className="space-y-2">
        {items.map(item => (
          <div key={item.key} className="bg-white border border-brand-border rounded-2xl p-3.5" data-testid={`sys-email-${item.key}`}>
            <div className="flex items-start gap-3">
              <button
                onClick={() => toggleEnabled(item)}
                className={`mt-1 w-9 h-5 rounded-full transition-colors flex-shrink-0 relative ${item.enabled ? 'bg-brand-green' : 'bg-brand-border'}`}
                title={item.enabled ? 'Ativo' : 'Desativado'}
                data-testid={`toggle-${item.key}`}
              >
                <span className={`absolute top-0.5 ${item.enabled ? 'left-[18px]' : 'left-0.5'} w-4 h-4 rounded-full bg-white transition-all`} />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-medium text-brand-text">{item.label}</h3>
                  {item.is_customized && <span className="text-[9px] bg-brand-mint/15 text-brand-green px-2 py-0.5 rounded-full uppercase tracking-wider font-medium">personalizado</span>}
                  {!item.enabled && <span className="text-[9px] bg-red-50 text-red-600 px-2 py-0.5 rounded-full uppercase tracking-wider font-medium">desativado</span>}
                </div>
                <p className="text-[11px] text-brand-muted mt-0.5">{item.description}</p>
                <p className="text-[10px] text-brand-placeholder mt-1"><b>Para:</b> {item.recipient} · <b>Trigger:</b> {item.trigger}</p>
                <p className="text-[10px] text-brand-placeholder mt-0.5 truncate"><b>Assunto:</b> {item.subject}</p>
              </div>
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button onClick={() => openPreview(item.key)} className="text-brand-muted hover:text-brand-green p-1.5" title="Pré-visualizar" data-testid={`preview-${item.key}`}><Eye size={14} /></button>
                <button onClick={() => openEdit(item)} className="text-brand-muted hover:text-brand-green p-1.5" title="Editar" data-testid={`edit-${item.key}`}><PencilSimple size={14} /></button>
                {item.is_customized && (
                  <button onClick={() => resetTemplate(item.key)} className="text-brand-muted hover:text-amber-600 p-1.5" title="Repor original" data-testid={`reset-${item.key}`}><ArrowCounterClockwise size={14} /></button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editKey} onOpenChange={(v) => { if (!v) setEditKey(null); }}>
        <DialogContent className="rounded-3xl border-brand-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{currentEditItem?.label || 'Editar email'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="bg-brand-bg/60 rounded-xl p-3 text-[11px] text-brand-muted">
              <p><b>Variáveis disponíveis:</b> {(currentEditItem?.vars || []).map(v => `{${v}}`).join(', ')}</p>
              <p className="mt-1 text-brand-placeholder">Usa as variáveis entre chavetas (ex: <code>{'{first_name}'}</code>), serão substituídas no momento do envio.</p>
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Assunto</Label>
              <Input value={editForm.subject} onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })} className="rounded-xl border-brand-border text-sm" data-testid="edit-subject-input" />
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Corpo (HTML)</Label>
              <Textarea value={editForm.body} onChange={(e) => setEditForm({ ...editForm, body: e.target.value })} rows={14} className="rounded-xl border-brand-border text-xs font-mono" data-testid="edit-body-input" />
            </div>
            <label className="flex items-center gap-2 text-xs text-brand-text cursor-pointer">
              <input type="checkbox" checked={editForm.enabled} onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })} className="rounded" data-testid="edit-enabled-checkbox" />
              <span>Email ativo</span>
            </label>
            <div className="flex gap-2 pt-2">
              <button onClick={previewFromEditor} className="flex-1 border border-brand-border text-brand-text rounded-full py-2.5 text-sm font-medium flex items-center justify-center gap-2" data-testid="preview-from-edit-btn"><Eye size={14} /> Pré-visualizar</button>
              <button onClick={saveEdit} className="flex-1 bg-brand-green text-white rounded-full py-2.5 text-sm font-medium flex items-center justify-center gap-2" data-testid="save-template-btn"><Check size={14} /> Guardar</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!previewKey} onOpenChange={(v) => { if (!v) { setPreviewKey(null); setPreviewHtml(''); } }}>
        <DialogContent className="rounded-3xl border-brand-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Pré-visualização</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="bg-brand-bg/60 rounded-xl p-3">
              <p className="text-[10px] uppercase tracking-wider text-brand-muted">Assunto</p>
              <p className="text-sm text-brand-text mt-0.5">{previewSubject}</p>
            </div>
            <div className="border border-brand-border rounded-xl overflow-hidden bg-white">
              <iframe
                title="email-preview"
                srcDoc={previewHtml}
                sandbox=""
                className="w-full"
                style={{ height: '520px', border: 'none' }}
                data-testid="preview-iframe"
              />
            </div>
            <p className="text-[10px] text-brand-placeholder text-center">Visualização com variáveis de exemplo. Os valores reais são substituídos no envio.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
